import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const GOOGLE_AUTH = "https://auth.emergentagent.com/";

export default function Login() {
  const { t } = useI18n();
  const { login, verifyOtp, resendOtp } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // 2FA state
  const [otpToken, setOtpToken] = useState(null);
  const [emailHint, setEmailHint] = useState("");
  const [code, setCode] = useState("");
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.otpRequired) {
        setOtpToken(res.otpToken);
        setEmailHint(res.emailHint || email);
        toast.success("Te hemos enviado un código de verificación por email");
      } else {
        toast.success(t("saved"));
        nav("/app");
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyOtp(otpToken, code.trim());
      toast.success(t("saved"));
      nav("/app");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  const resend = async () => {
    setResending(true);
    try {
      const newToken = await resendOtp(otpToken);
      setOtpToken(newToken);
      setCode("");
      toast.success("Nuevo código enviado a tu email");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setResending(false); }
  };

  const cancelOtp = () => {
    setOtpToken(null);
    setCode("");
    setEmailHint("");
  };

  const google = () => {
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `${GOOGLE_AUTH}?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img src="https://images.pexels.com/photos/7789620/pexels-photo-7789620.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
             alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="relative z-10 p-10 text-primary-foreground h-full flex flex-col justify-end">
          <img src="/dr-dimy-logo.jpeg" alt="Dr. Dimy Laguerre" className="w-16 h-16 rounded-2xl mb-4 ring-2 ring-white/40" />
          <h2 className="font-display text-3xl">Dr. Dimy Laguerre</h2>
          <p className="opacity-80 mt-2">{t("tagline")}</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        {!otpToken ? (
          <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Dr. Dimy Laguerre</Link>
            <h1 className="font-display text-3xl mt-4 mb-1">{t("login")}</h1>
            <p className="text-sm text-muted-foreground mb-6">{t("welcome")}</p>

            <Button type="button" variant="outline" className="w-full mb-4" onClick={google} data-testid="google-login-btn">
              <svg width="16" height="16" viewBox="0 0 48 48" className="mr-2"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.7 1.1 7.8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.1 35.4 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41.3 35.4 44 30 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
              {t("googleLogin")}
            </Button>
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground uppercase">{t("or")}</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <div className="space-y-3">
              <div>
                <Label>{t("email")}</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
              </div>
              <div>
                <Label>{t("password")}</Label>
                <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading} data-testid="login-submit-btn">
                {loading ? "..." : t("login")}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-6 text-center">
              <Link to="/register" className="text-primary hover:underline" data-testid="link-register">{t("register")}</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={submitOtp} className="w-full max-w-sm fade-up" data-testid="otp-form">
            <button type="button" onClick={cancelOtp} className="text-sm text-muted-foreground hover:text-foreground" data-testid="otp-back">
              ← Volver
            </button>
            <div className="mt-4 mb-1 flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </div>
              <h1 className="font-display text-2xl">Verificación en 2 pasos</h1>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Hemos enviado un código de 6 dígitos a <b>{emailHint}</b>. Introduce el código para acceder.
            </p>
            <div className="space-y-3">
              <div>
                <Label>Código de verificación</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="text-center font-mono text-2xl tracking-[0.5em]"
                  data-testid="otp-code-input"
                />
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading || code.length !== 6} data-testid="otp-verify-btn">
                {loading ? "..." : "Verificar y entrar"}
              </Button>
              <Button type="button" variant="outline" className="w-full" onClick={resend} disabled={resending} data-testid="otp-resend-btn">
                {resending ? "Enviando..." : "Reenviar código"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-6 text-center">
              El código expira en 10 minutos. Si no lo recibes, revisa tu carpeta de spam.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
